import { useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionApi } from '../api/subscription.api';
import type { ExtendCommitmentRequest } from '@platform/billing';
import { subscriptionKeys } from '../api/subscription.keys';

/**
 * 구독 생명주기 액션 훅(§5/§7) — 해지 신청·구독해지 취소·청약철회·약정 연장.
 *
 * 개인 구독을 기업 좌석으로 덮어 정리하는 액션은 없다 — 개인 계정과 기업 계정이
 * 생성 시점에 갈라져 한 사람이 양쪽을 동시에 가질 수 없다.
 *
 * ── mutation 객체를 그대로 노출한다 ──────────────────────────────────────
 * `mutate` 는 reject 하지 않는다. 성공 후 동작은 `onSuccess` 에 넣고, 실패는
 * QueryClient 의 전역 mutation 에러 핸들러가 서버 메시지로 알린다.
 *
 *   requestCancellation.mutate({ id }, { onSuccess: () => onClose() });
 *
 * `mutateAsync` 는 **결과를 다음 단계로 넘겨야 할 때만** 쓴다(그때의 try/catch 는
 * 회피가 아니라 분기 처리다). 여기 액션들은 전부 그런 경우가 아니다.
 *
 * 성공 시 구독 조회 캐시(subscriptionKeys.all)를 무효화해 화면 상태가 즉시 갱신된다.
 */
export function useSubscriptionLifecycle() {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: subscriptionKeys.all });

  /** 해지 신청 접수(약정=cancel_requested / 무약정=주기말 해지). */
  const requestCancellation = useMutation({
    mutationFn: ({ id, memo }: { id: string; memo?: string }) =>
      subscriptionApi.requestCancellation(id, memo),
    onSuccess: invalidate,
  });

  /** 구독해지 취소(철회) — 확정 전(주기말 예약/승인 대기)까지만 가능. */
  const revokeCancellation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      subscriptionApi.revokeCancellation(id),
    onSuccess: invalidate,
  });

  /**
   * 청약철회(§6.1) — 전액 환불 + 즉시 종료.
   * 개인 구독·미사용·7일 이내에만 서버가 통과시킨다. 비율은 서버가 정한다.
   */
  const requestWithdrawal = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      subscriptionApi.requestWithdrawal(id),
    // 라이선스도 함께 회수되므로 구독 캐시만으로는 화면이 낡는다.
    onSuccess: invalidate,
  });

  /** 개인 연간 중도해지 신청 — 접수만 한다. 환불은 운영자 승인 때 실행된다. */
  const requestAnnualTermination = useMutation({
    mutationFn: ({ id, memo }: { id: string; memo?: string }) =>
      subscriptionApi.requestAnnualTermination(id, memo),
    onSuccess: invalidate,
  });

  /** 약정 연장(term 재선택 + 동의). */
  const extendCommitment = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: ExtendCommitmentRequest;
    }) => subscriptionApi.extendCommitment(id, body),
    onSuccess: invalidate,
  });

  return {
    /** 어느 액션이든 진행 중이면 true — 버튼 비활성/스피너용. */
    busy:
      requestCancellation.isPending ||
      revokeCancellation.isPending ||
      requestWithdrawal.isPending ||
      requestAnnualTermination.isPending ||
      extendCommitment.isPending,
    requestCancellation,
    revokeCancellation,
    requestWithdrawal,
    requestAnnualTermination,
    extendCommitment,
  };
}
